import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { API_BASE_URL } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  ArrowLeft,
  MoreHorizontal,
  PauseCircle,
  PlayCircle,
  Trash,
  RefreshCw,
  MailCheck,
  MailX,
  Instagram,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useDeleteCreator, useEnrichCreatorInstagram, useUpdateCreator } from '@/hooks/use-creators'
import type { CreatorWithTier } from '@/lib/api/modules/creators'

const CREATOR_STATUS_COLORS: Record<string, string> = {
  prospecting: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  negotiating: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  churned: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
}

const CREATOR_STATUS_LABELS: Record<string, string> = {
  prospecting: 'Prospecção',
  negotiating: 'Negociação',
  active: 'Ativo',
  paused: 'Pausado',
  churned: 'Churn',
}

interface CreatorHeaderProps {
  creator: CreatorWithTier
}

/**
 * Render a header displaying a creator's avatar, name, status badge, contact and tier information, and action controls.
 *
 * @param creator - The creator record with optional tier information used to populate the avatar, labels, and metadata
 * @returns A JSX element containing the creator header with actions (activate/pause button and dropdown menu)
 */
export function CreatorHeader({ creator }: CreatorHeaderProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const navigate = useNavigate()
  const enrichIg = useEnrichCreatorInstagram()
  const updateCreator = useUpdateCreator()
  const deleteCreator = useDeleteCreator()

  const initials = creator.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  const isActive = creator.status === 'active'

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
        <Avatar className="h-16 w-16">
          <AvatarImage
            src={`${API_BASE_URL}/api/admin/creators/${creator.id}/avatar?v=${creator.updatedAt}`}
            alt={creator.name}
          />
          <AvatarFallback className="text-lg bg-primary/10 text-primary">{initials}</AvatarFallback>
        </Avatar>

        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight truncate">
              {creator.name}
            </h1>
            <Badge
              variant="secondary"
              className={`shrink-0 ${CREATOR_STATUS_COLORS[creator.status] || ''}`}
            >
              {CREATOR_STATUS_LABELS[creator.status] || creator.status}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
            {creator.instagramUsername && (
              <a
                href={`https://instagram.com/${creator.instagramUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 truncate hover:text-foreground transition-colors"
              >
                <Instagram className="h-3.5 w-3.5 text-pink-500 shrink-0" />
                <span>@{creator.instagramUsername}</span>
                {creator.instagramFollowers != null && (
                  <span className="text-xs">
                    ({new Intl.NumberFormat('pt-BR').format(creator.instagramFollowers)})
                  </span>
                )}
              </a>
            )}
            {creator.tiktokUsername && (
              <a
                href={`https://tiktok.com/@${creator.tiktokUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 truncate hover:text-foreground transition-colors"
              >
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm bg-black text-[7px] font-bold text-white dark:bg-white dark:text-black">
                  TK
                </span>
                <span>@{creator.tiktokUsername}</span>
                {creator.tiktokFollowers != null && (
                  <span className="text-xs">
                    ({new Intl.NumberFormat('pt-BR').format(creator.tiktokFollowers)})
                  </span>
                )}
              </a>
            )}
            {creator.email && (
              <>
                <span className="hidden md:inline">•</span>
                <span className="truncate inline-flex items-center gap-1">
                  {creator.email}
                  {creator.emailVerifiedAt ? (
                    <MailCheck className="h-3.5 w-3.5 text-green-600 shrink-0" />
                  ) : (
                    <MailX className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )}
                </span>
              </>
            )}
            <span className="hidden md:inline">•</span>
            <span className="font-medium text-foreground shrink-0">
              {creator.commissionTier?.name || 'Sem Tier'}
            </span>
            {creator.commissionTier && (
              <span className="text-xs shrink-0">
                ({(creator.commissionTier.commissionRate * 100).toFixed(0)}% comissão)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {creator.instagramUsername && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <ChonkyButton
                  variant="secondary"
                  size="icon"
                  onClick={() => enrichIg.mutate(creator.id)}
                  loading={enrichIg.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                </ChonkyButton>
              </TooltipTrigger>
              <TooltipContent>Atualizar dados do Instagram</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <ChonkyButton
          variant={isActive ? 'secondary' : 'primary'}
          loading={updateCreator.isPending}
          onClick={() =>
            updateCreator.mutate({
              id: creator.id,
              data: { status: isActive ? 'paused' : 'active' },
            })
          }
        >
          {isActive ? (
            <>
              <PauseCircle className="h-4 w-4" />
              Pausar Creator
            </>
          ) : (
            <>
              <PlayCircle className="h-4 w-4" />
              Ativar Creator
            </>
          )}
        </ChonkyButton>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <span className="sr-only">Abrir menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash className="mr-2 h-4 w-4" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir creator</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir <strong>{creator.name}</strong>?
                <br />
                Essa ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleteCreator.isPending}
                onClick={(e) => {
                  e.preventDefault()
                  deleteCreator.mutate(creator.id, {
                    onSuccess: () => navigate({ to: '/creators' }),
                  })
                }}
              >
                {deleteCreator.isPending ? 'Excluindo...' : 'Excluir'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
